import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeSlashIcon,
  LockClosedIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import Avatar from '@/components/Avatar';
import { useToast } from '@/components/Toast';
import type { SidebarGroup } from '@/features/groupRail/GroupRailListItem';
import { usePollDetail } from '@/hooks/usePolls';
import { useGroupLeadership } from '@/hooks/useGroupLeadership';
import PollOptionRow from './PollOptionRow';
import PollLeaderActions from './PollLeaderActions';
import { getTimeRemaining } from './PollCard';

interface Props {
  group: SidebarGroup;
  pollId: string;
}

/** Poll detail: header, options/voting, results, participants, leader actions. */
export default function PollDetailPane({ group, pollId }: Props) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isLeader = useGroupLeadership(group.id);
  const detail = usePollDetail(pollId, group.orgId);
  const { poll, loading, error, refetch, vote, retractVote, deleteOption, isMutating } = detail;

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const isClosed = poll?.status === 'closed' || poll?.status === 'archived';
  const hasVoted = poll?.has_voted ?? false;
  const userSelections = poll?.user_selections ?? [];

  const canSeeResults = useMemo(() => {
    if (!poll) return false;
    return isLeader || isClosed || poll.show_results_before_close;
  }, [poll, isLeader, isClosed]);

  // vote_count is absent for non-leaders on hidden-results active polls
  const totalVotes = useMemo(
    () => (poll?.options ?? []).reduce((sum, o) => sum + (o.vote_count ?? 0), 0),
    [poll?.options]
  );

  const toggleSelection = (optionId: string) => {
    if (isClosed) return;
    setSelectedOptions((prev) => {
      if (poll?.allow_multiple_choices) {
        return prev.includes(optionId)
          ? prev.filter((id) => id !== optionId)
          : [...prev, optionId];
      }
      return prev.includes(optionId) ? [] : [optionId];
    });
  };

  const handleVote = async () => {
    if (selectedOptions.length === 0) return;
    try {
      await vote(selectedOptions);
      setSelectedOptions([]);
    } catch (e: any) {
      showToast(
        e?.response?.data?.message || 'Something went wrong. Please try again.',
        'error'
      );
    }
  };

  const handleChangeVote = async () => {
    try {
      await retractVote();
      setSelectedOptions([]);
    } catch {
      showToast(
        'Anonymous votes are permanent and cannot be changed to protect voter privacy.',
        'error'
      );
    }
  };

  const handleDeleteOption = async (optionId: string, label: string) => {
    if (!window.confirm(`Delete option "${label}"?`)) return;
    try {
      await deleteOption(optionId);
    } catch (e: any) {
      showToast(
        e?.response?.data?.message || 'Options with votes cannot be deleted.',
        'error'
      );
    }
  };

  const backLink = (
    <button
      onClick={() => navigate(`/polls/${group.id}`)}
      className="flex items-center gap-1.5 text-sm font-medium text-flock-navy-500 transition hover:text-flock-navy-700"
    >
      <ArrowLeftIcon className="h-4 w-4" />
      Back to polls
    </button>
  );

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b border-flock-gray-200 bg-white px-6 py-4">{backLink}</div>
        <div className="flex flex-1 items-center justify-center text-sm text-flock-navy-400">
          Loading poll…
        </div>
      </div>
    );
  }

  if (error || !poll) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b border-flock-gray-200 bg-white px-6 py-4">{backLink}</div>
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <p className="text-sm text-flock-navy-500">{error || 'Poll not found'}</p>
          <button
            onClick={() => refetch()}
            className="rounded-lg bg-flock-navy-600 px-4 py-2 text-sm font-medium text-white hover:bg-flock-navy-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-flock-gray-200 bg-white px-6 py-4">{backLink}</div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Header */}
          <div className="rounded-xl border border-flock-gray-200 bg-white p-5">
            <h1 className="text-lg font-bold text-flock-navy-900">{poll.question}</h1>
            {poll.description && (
              <p className="mt-2 whitespace-pre-wrap text-base text-flock-navy-600">
                {poll.description}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {poll.is_anonymous && (
                <span className="flex items-center gap-1 rounded-full bg-flock-gray-100 px-2 py-1 text-xs text-flock-navy-500">
                  <EyeSlashIcon className="h-3.5 w-3.5" />
                  Anonymous
                </span>
              )}
              {poll.allow_multiple_choices && (
                <span className="flex items-center gap-1 rounded-full bg-flock-gray-100 px-2 py-1 text-xs text-flock-navy-500">
                  <Squares2X2Icon className="h-3.5 w-3.5" />
                  Multiple
                </span>
              )}
              <span
                className={
                  isClosed
                    ? 'flex items-center gap-1 rounded-full bg-red-50 px-2 py-1 text-xs text-red-600'
                    : 'flex items-center gap-1 rounded-full bg-green-50 px-2 py-1 text-xs text-green-700'
                }
              >
                {isClosed ? (
                  <LockClosedIcon className="h-3.5 w-3.5" />
                ) : (
                  <ClockIcon className="h-3.5 w-3.5" />
                )}
                {isClosed ? 'Closed' : getTimeRemaining(poll.closes_at)}
              </span>
            </div>
            {poll.creator_name && (
              <p className="mt-2 text-xs text-flock-navy-400">Created by {poll.creator_name}</p>
            )}
          </div>

          {/* Results hidden message */}
          {!canSeeResults && hasVoted && (
            <div className="flex items-center gap-2 rounded-xl border border-flock-gray-200 bg-white p-4 text-flock-navy-500">
              <EyeSlashIcon className="h-5 w-5" />
              <span>Results hidden until poll closes</span>
            </div>
          )}

          {/* Options */}
          <div className="space-y-2">
            {poll.options?.map((option) => (
              <PollOptionRow
                key={option.id}
                option={option}
                poll={poll}
                totalVotes={totalVotes}
                canSeeResults={canSeeResults}
                hasVoted={hasVoted}
                isSelected={
                  hasVoted ? userSelections.includes(option.id) : selectedOptions.includes(option.id)
                }
                isSelecting={!hasVoted && !isClosed}
                allowMultiple={poll.allow_multiple_choices}
                onToggle={() => toggleSelection(option.id)}
                onDelete={
                  isLeader && !isClosed
                    ? () => handleDeleteOption(option.id, option.label)
                    : undefined
                }
              />
            ))}
          </div>

          {/* Vote / Change Vote */}
          {!isClosed && (
            <div>
              {!hasVoted ? (
                <>
                  {poll.is_anonymous && (
                    <p className="mb-2 text-center text-xs text-flock-navy-400">
                      * This is an anonymous poll. Your vote is permanent and cannot be changed.
                    </p>
                  )}
                  <button
                    onClick={handleVote}
                    disabled={selectedOptions.length === 0 || isMutating}
                    className="min-h-touch w-full rounded-xl bg-flock-navy-600 py-3.5 text-base font-semibold text-white transition hover:bg-flock-navy-700 disabled:bg-flock-gray-300"
                  >
                    {isMutating ? 'Voting…' : 'Vote'}
                  </button>
                </>
              ) : !poll.is_anonymous ? (
                <button
                  onClick={handleChangeVote}
                  disabled={isMutating}
                  className="min-h-touch w-full rounded-xl border border-flock-navy-600 py-3 text-base font-semibold text-flock-navy-600 transition hover:bg-flock-navy-50 disabled:opacity-50"
                >
                  {isMutating ? 'Working…' : 'Change Vote'}
                </button>
              ) : null}
            </div>
          )}

          {/* Anonymous vote confirmation */}
          {poll.is_anonymous && hasVoted && !isClosed && (
            <div className="flex items-center gap-2 rounded-xl border border-flock-gray-200 bg-white p-4 text-flock-navy-600">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span>Your anonymous vote has been recorded</span>
            </div>
          )}

          {/* Participants */}
          {!poll.is_anonymous && poll.participants && poll.participants.length > 0 && (
            <div className="rounded-xl border border-flock-gray-200 bg-white p-5">
              <h2 className="mb-3 text-base font-semibold text-flock-navy-700">
                Participants ({poll.participants.length})
              </h2>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {poll.participants.map((p) => (
                  <span key={p.id} className="flex items-center gap-2">
                    <Avatar
                      src={p.user_profile_photo}
                      name={p.user_name}
                      sizeClass="h-6 w-6"
                      textClass="text-[10px]"
                      bgClass="bg-flock-navy-500"
                    />
                    <span className="text-sm text-flock-navy-600">{p.user_name}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Leader actions */}
          {isLeader && (
            <PollLeaderActions
              poll={poll}
              detail={detail}
              onArchivedOrDeleted={() => navigate(`/polls/${group.id}`)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
